import { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReportCard from "components/ReportCard";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { IOeeFailureTypeData } from "api/queries/millDashboardAPI";
import { ISelectedTrendGraphItem } from "contexts/TrendReportContext/TrendReportContextInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { buildDowntimeTopFive } from "utilities/oeeSummary";
import createOeeDowntimeTopFiveSegmentBar, { toSegmentKey } from "./components/OeeDowntimeTopFiveSegmentBar";
import { useStyles } from "./OeeDowntimeTopFiveByMachineCenter.styles";
import {
    IOeeDowntimeTopFiveByMachineCenterProps,
    IOeeDowntimeTopFiveChartData,
    TOeeDowntimeTopFiveChartRow,
} from "./OeeDowntimeTopFiveByMachineCenterInterfaces";

/**
 * IOeeFailureTypeData is not in translateDefaultEntity's Entity union (its translation language has no
 * `code`), yet the helper only reads `name` and `language.language_id`, which the OEE query returns.
 */
const asTranslatableFailureType = (failureType: IOeeFailureTypeData | undefined) =>
    failureType as ISelectedTrendGraphItem | undefined;

const OeeDowntimeTopFiveByMachineCenter = ({
    loading,
    productionLineName,
    rows,
}: IOeeDowntimeTopFiveByMachineCenterProps) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    const { chartRows, secondsBySegment } = useMemo<IOeeDowntimeTopFiveChartData>(() => {
        const machineCenterNames = new Map(rows.map((row) => [row.machineCenterId, row.machineCenterName]));
        const failureTypes = new Map(rows.map((row) => [row.failureTypeId, row.failureType]));
        const segmentSeconds = new Map<string, number>();

        const barRows = buildDowntimeTopFive(rows).bars.map((bar) =>
            bar.segments.reduce<TOeeDowntimeTopFiveChartRow>(
                (chartRow, segment) => {
                    const failureType = failureTypes.get(segment.failureTypeId);
                    const failureTypeName = translateDefaultEntity(
                        i18n.language,
                        asTranslatableFailureType(failureType),
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                    );
                    const segmentKey = toSegmentKey(bar.machineCenterId, failureTypeName);
                    segmentSeconds.set(segmentKey, (segmentSeconds.get(segmentKey) ?? 0) + segment.seconds);

                    return {
                        ...chartRow,
                        [failureTypeName]: Number(chartRow[failureTypeName] ?? 0) + segment.percentOfTotal,
                        [`${failureTypeName}Color`]: failureType?.color_code ?? "",
                    };
                },
                {
                    machineCenterId: bar.machineCenterId,
                    machineCenter: machineCenterNames.get(bar.machineCenterId) ?? "",
                    totalDowntimeByMill: bar.percentOfTotal,
                },
            ),
        );

        return {
            chartRows: barRows.sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill)),
            secondsBySegment: segmentSeconds,
        };
    }, [rows, i18n.language]);

    const SegmentBar = useMemo(() => createOeeDowntimeTopFiveSegmentBar(secondsBySegment), [secondsBySegment]);

    return (
        <ReportCard
            title={
                productionLineName
                    ? t("locations.oee_summary_dashboard.downtime_top_five.title_with_production_line", {
                          productionLine: productionLineName,
                      })
                    : t("locations.oee_summary_dashboard.downtime_top_five.title")
            }
            showNoData={!loading && chartRows.length === 0}
        >
            {loading ? (
                <div className={classes.loadingWrapper}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <div className={classes.chartWrapper}>
                    <ReportDowntimeBarChart
                        data={chartRows}
                        index="machineCenter"
                        showPercentLayerOnTotalBar
                        noTrend
                        customBarComponent={SegmentBar}
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default OeeDowntimeTopFiveByMachineCenter;
